import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactDOM from 'react-dom/client';
import axios from 'axios';
import Select from 'react-select';
import { FaPlus, FaDownload, FaExternalLinkAlt, FaRedo, FaClipboardCheck } from 'react-icons/fa';
import { useAuth } from '../AuthContext';
import InspectionPrintSheet from './InspectionPrintSheet';
import { generateInspectionPDF } from './inspectionPDFGenerator';
import { fetchCompanyInfo } from '../../../utils/companyInfo';
const { rootUrl } = require('../../../config/config');

const PAGE_SIZE = 20;

const selectStyles = {
  control: (base) => ({
    ...base,
    minHeight: '38px',
    fontSize: '14px',
    borderColor: '#d1d5db',
    borderRadius: '6px',
    boxShadow: 'none',
    '&:hover': { borderColor: '#9ca3af' },
  }),
  option: (base, { isFocused }) => ({
    ...base,
    fontSize: '14px',
    backgroundColor: isFocused ? '#fff5f2' : 'white',
    color: '#111827',
  }),
};

// ─── ActionButtons ────────────────────────────────────────────────────────────

const ActionButtons = ({ inspection, onPDF, onRetryDrive, onConduct }) => {
  const hasDriveFolder = !!inspection.metadata?.drive_folder_id;
  const isCompleted = inspection.inspection_status === 'completed';

  return (
    <div className="flex flex-wrap gap-1.5">
      <button
        onClick={() => onPDF(inspection)}
        className="flex items-center gap-1 bg-gray-600 text-white px-2.5 py-1.5 text-xs font-medium hover:bg-gray-700 transition-colors rounded"
      >
        <FaDownload className="w-3 h-3" /> PDF
      </button>

      {hasDriveFolder ? (
        <a
          href={inspection.metadata.drive_folder_link}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1 bg-blue-600 text-white px-2.5 py-1.5 text-xs font-medium hover:bg-blue-700 transition-colors rounded"
        >
          Drive <FaExternalLinkAlt className="w-2.5 h-2.5" />
        </a>
      ) : (
        !isCompleted && (
          <button
            onClick={() => onRetryDrive(inspection)}
            className="flex items-center gap-1 bg-yellow-500 text-white px-2.5 py-1.5 text-xs font-medium hover:bg-yellow-600 transition-colors rounded"
          >
            <FaRedo className="w-3 h-3" /> Retry Drive
          </button>
        )
      )}

      {!isCompleted && (
        <button
          onClick={() => onConduct(inspection)}
          className="flex items-center gap-1 bg-brand-primary text-white px-2.5 py-1.5 text-xs font-medium hover:opacity-90 transition-opacity rounded"
        >
          <FaClipboardCheck className="w-3 h-3" /> Conduct
        </button>
      )}
    </div>
  );
};

// ─── InternalInspections ─────────────────────────────────────────────────────

const InternalInspections = () => {
  const { token, user } = useAuth();
  const navigate = useNavigate();

  const [inspections, setInspections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pdfError, setPdfError] = useState('');
  const [success, setSuccess] = useState('');

  const [searchTerm, setSearchTerm] = useState('');
  const [resultFilter, setResultFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [dateRange, setDateRange] = useState('');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  const authHeaders = { Authorization: `Bearer ${token}` };

  const fetchInspections = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${rootUrl}/cars/inspections/internal`, { headers: authHeaders });
      setInspections(res.data);
      setError('');
    } catch (err) {
      setError('Failed to fetch inspections.');
    } finally {
      setLoading(false);
    }
  };

  const fetchInspectionDetails = async (inspectionId) => {
    const res = await axios.get(`${rootUrl}/cars/inspections/internal/${inspectionId}`, { headers: authHeaders });
    return res.data;
  };

  const handleDownloadPDF = async (inspection) => {
    setPdfError('');
    try {
      const details = await fetchInspectionDetails(inspection.id);
      const companyInfo = await fetchCompanyInfo(token);
      const tempDiv = document.createElement('div');
      tempDiv.style.cssText = 'position:absolute;left:-9999px;width:210mm';
      document.body.appendChild(tempDiv);
      const root = ReactDOM.createRoot(tempDiv);
      root.render(<InspectionPrintSheet inspection={details} companyInfo={companyInfo} />);
      await new Promise(r => setTimeout(r, 800));
      await generateInspectionPDF(details);
      root.unmount();
      document.body.removeChild(tempDiv);
    } catch (err) {
      setPdfError('Failed to generate PDF. Please try again.');
    }
  };

  const handleRetryDriveSetup = async (inspection) => {
    try {
      const res = await axios.post(
        `${rootUrl}/cars/inspections/internal/${inspection.id}/setup-drive`,
        {},
        { headers: authHeaders }
      );
      if (res.data.success) {
        setSuccess('Drive folder created successfully.');
        fetchInspections();
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create Drive folder.');
    }
  };

  useEffect(() => { fetchInspections(); }, []);

  const getDateBounds = () => {
    const now = new Date();
    const startOf = (unit) => {
      const d = new Date(now);
      if (unit === 'week') d.setDate(d.getDate() - d.getDay());
      if (unit === 'month') d.setDate(1);
      d.setHours(0, 0, 0, 0);
      return d;
    };
    switch (dateRange) {
      case 'today':  return { from: new Date(new Date().setHours(0,0,0,0)),   to: new Date(new Date().setHours(23,59,59,999)) };
      case 'week':   return { from: startOf('week'),                           to: now };
      case 'month':  return { from: startOf('month'),                          to: now };
      case 'last30': return { from: new Date(now - 30 * 86400000),             to: now };
      case 'last90': return { from: new Date(now - 90 * 86400000),             to: now };
      case 'custom': return {
        from: customFrom ? new Date(customFrom) : null,
        to:   customTo   ? new Date(new Date(customTo).setHours(23,59,59,999)) : null,
      };
      default: return { from: null, to: null };
    }
  };

  const filteredInspections = inspections.filter(ins => {
    const search = searchTerm.toLowerCase();
    if (search && !ins.plate_no?.toLowerCase().includes(search) && !ins.inspector_name?.toLowerCase().includes(search)) return false;
    if (resultFilter && ins.result !== resultFilter) return false;
    if (statusFilter && ins.inspection_status !== statusFilter) return false;
    if (dateRange) {
      const { from, to } = getDateBounds();
      const d = ins.inspection_date ? new Date(ins.inspection_date) : null;
      if (d) {
        if (from && d < from) return false;
        if (to   && d > to)   return false;
      }
    }
    return true;
  });

  const totalPages           = Math.ceil(filteredInspections.length / PAGE_SIZE);
  const paginatedInspections = filteredInspections.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const showingFrom          = filteredInspections.length === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
  const showingTo            = Math.min(currentPage * PAGE_SIZE, filteredInspections.length);
  const hasActiveFilters     = !!(searchTerm || resultFilter || statusFilter || dateRange);

  const clearFilters = () => {
    setSearchTerm(''); setResultFilter(''); setStatusFilter('');
    setDateRange(''); setCustomFrom(''); setCustomTo('');
    setCurrentPage(1);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const resultBadgeClass = (result) => {
    switch (result?.toLowerCase()) {
      case 'passed':      return 'bg-green-100 text-green-800';
      case 'failed':      return 'bg-red-100 text-red-800';
      case 'conditional': return 'bg-yellow-100 text-yellow-800';
      case 'invalid':     return 'bg-red-800 text-white';
      default:            return 'bg-gray-100 text-gray-700';
    }
  };

  const statusBadgeClass = (status) => {
    switch (status?.toLowerCase()) {
      case 'in_progress': return 'bg-blue-100 text-blue-800';
      case 'completed':   return 'bg-green-100 text-green-800';
      case 'scheduled':   return 'bg-yellow-100 text-yellow-800';
      case 'cancelled':   return 'bg-gray-100 text-gray-700';
      default:            return 'bg-gray-100 text-gray-700';
    }
  };

  const statusLabel = (status) => {
    switch (status?.toLowerCase()) {
      case 'in_progress': return 'In Progress';
      case 'completed':   return 'Completed';
      case 'scheduled':   return 'Scheduled';
      case 'cancelled':   return 'Cancelled';
      default:            return status || '—';
    }
  };

  const resultLabel = (result) => {
    if (!result) return 'Pending';
    return result.charAt(0).toUpperCase() + result.slice(1);
  };

  return (
    <div className="w-full max-w-7xl mx-auto px-4 py-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Internal Inspections</h1>
        <button
          onClick={() => navigate('/inspections/internal/new')}
          className="flex items-center gap-2 bg-brand-primary text-white px-4 py-2 text-sm font-medium hover:opacity-90 transition-opacity rounded"
        >
          <FaPlus className="w-3 h-3" />
          <span className="hidden sm:inline">Create Inspection</span>
          <span className="sm:hidden">Create</span>
        </button>
      </div>

      {pdfError && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 text-sm flex justify-between items-start rounded">
          {pdfError}
          <button onClick={() => setPdfError('')} className="ml-4 text-red-400 hover:text-red-600">✕</button>
        </div>
      )}

      {success && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 text-green-700 text-sm flex justify-between items-start rounded">
          {success}
          <button onClick={() => setSuccess('')} className="ml-4 text-green-400 hover:text-green-600">✕</button>
        </div>
      )}

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded">
          {error}
        </div>
      )}

      {loading ? (
        <>
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-3 sm:p-4 mb-5 flex flex-wrap gap-2">
            {[...Array(4)].map((_, i) => <div key={i} className="bg-gray-100 animate-pulse rounded-md h-9 w-36" />)}
          </div>
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 sm:p-6 space-y-3">
            {[...Array(5)].map((_, i) => <div key={i} className="bg-gray-100 animate-pulse rounded-xl h-12" />)}
          </div>
        </>
      ) : (
        <>
          {/* Filter bar */}
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-3 sm:p-4 mb-5">
          <div className="flex flex-wrap gap-2 items-end">
            <div className="w-full sm:w-56">
              <input
                type="text"
                placeholder="Plate or inspector..."
                value={searchTerm}
                onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-brand-primary"
              />
            </div>

            <div className="w-full sm:w-40">
              <Select
                styles={selectStyles}
                placeholder="All Results"
                isClearable
                value={resultFilter ? { value: resultFilter, label: resultFilter.charAt(0).toUpperCase() + resultFilter.slice(1) } : null}
                onChange={opt => { setResultFilter(opt?.value || ''); setCurrentPage(1); }}
                options={[
                  { value: 'passed',      label: 'Passed' },
                  { value: 'failed',      label: 'Failed' },
                  { value: 'conditional', label: 'Conditional' },
                  { value: 'pending',     label: 'Pending' },
                  { value: 'invalid',     label: 'Invalid' },
                ]}
              />
            </div>

            <div className="w-full sm:w-40">
              <Select
                styles={selectStyles}
                placeholder="All Statuses"
                isClearable
                value={statusFilter ? { value: statusFilter, label: statusFilter === 'in_progress' ? 'In Progress' : statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1) } : null}
                onChange={opt => { setStatusFilter(opt?.value || ''); setCurrentPage(1); }}
                options={[
                  { value: 'in_progress', label: 'In Progress' },
                  { value: 'completed',   label: 'Completed' },
                  { value: 'scheduled',   label: 'Scheduled' },
                  { value: 'cancelled',   label: 'Cancelled' },
                ]}
              />
            </div>

            <div className="w-full sm:w-44">
              <Select
                styles={selectStyles}
                placeholder="All Time"
                isClearable
                value={dateRange ? { value: dateRange, label: { today: 'Today', week: 'This Week', month: 'This Month', last30: 'Last 30 Days', last90: 'Last 3 Months', custom: 'Custom' }[dateRange] } : null}
                onChange={opt => { setDateRange(opt?.value || ''); setCurrentPage(1); }}
                options={[
                  { value: 'today',  label: 'Today' },
                  { value: 'week',   label: 'This Week' },
                  { value: 'month',  label: 'This Month' },
                  { value: 'last30', label: 'Last 30 Days' },
                  { value: 'last90', label: 'Last 3 Months' },
                  { value: 'custom', label: 'Custom' },
                ]}
              />
            </div>

            {dateRange === 'custom' && (
              <>
                <input
                  type="date"
                  value={customFrom}
                  onChange={e => { setCustomFrom(e.target.value); setCurrentPage(1); }}
                  className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-brand-primary"
                />
                <input
                  type="date"
                  value={customTo}
                  onChange={e => { setCustomTo(e.target.value); setCurrentPage(1); }}
                  className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-brand-primary"
                />
              </>
            )}

            {hasActiveFilters && (
              <button onClick={clearFilters} className="text-sm text-brand-primary hover:underline whitespace-nowrap self-center">
                Clear all
              </button>
            )}
          </div>
          </div>

          {/* Mobile card list */}
          <div className="block md:hidden space-y-3">
            {paginatedInspections.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-500 text-sm mb-3">
                  {hasActiveFilters ? 'No inspections match your filters.' : 'No inspections found.'}
                </p>
                {hasActiveFilters
                  ? <button onClick={clearFilters} className="text-sm text-brand-primary hover:underline">Clear filters</button>
                  : <button onClick={() => navigate('/inspections/internal/new')} className="text-sm text-brand-primary hover:underline">Create one</button>
                }
              </div>
            ) : paginatedInspections.map(ins => (
              <div key={ins.id} className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
                <div className="flex justify-between items-start mb-2">
                  <span className="font-bold text-gray-900 text-base tracking-wide">{ins.plate_no?.toUpperCase()}</span>
                  <span className="text-xs text-gray-400">{formatDate(ins.inspection_date)}</span>
                </div>
                <div className="flex gap-2 flex-wrap mb-2">
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${resultBadgeClass(ins.result)}`}>{resultLabel(ins.result)}</span>
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${statusBadgeClass(ins.inspection_status)}`}>{statusLabel(ins.inspection_status)}</span>
                </div>
                <div className="text-xs text-gray-500 mb-1">
                  {[ins.make, ins.model, ins.year].filter(Boolean).join(' ')}{ins.inspector_name && ` · ${ins.inspector_name}`}
                </div>
                {ins.mileage && (
                  <div className="text-xs text-gray-500 mb-3">
                    {ins.mileage.toLocaleString()} km
                    {ins.distance_since_last != null && ` · +${ins.distance_since_last.toLocaleString()} km since last`}
                  </div>
                )}
                <ActionButtons
                  inspection={ins}
                  onPDF={handleDownloadPDF}
                  onRetryDrive={handleRetryDriveSetup}

                  onConduct={(ins) => navigate(`/inspections/internal/${ins.id}/conduct`)}
                />
              </div>
            ))}
          </div>

          {/* Desktop table */}
          <div className="hidden md:block bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="overflow-x-auto p-4 sm:p-6">
            {paginatedInspections.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-500 text-sm mb-3">
                  {hasActiveFilters ? 'No inspections match your filters.' : 'No inspections found.'}
                </p>
                {hasActiveFilters
                  ? <button onClick={clearFilters} className="text-sm text-brand-primary hover:underline">Clear filters</button>
                  : <button onClick={() => navigate('/inspections/internal/new')} className="text-sm text-brand-primary hover:underline">Create one</button>
                }
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b-2 border-gray-200 text-xs text-gray-500 uppercase tracking-wide text-left">
                    <th className="pb-3 pr-4 font-medium">Date</th>
                    <th className="pb-3 pr-4 font-medium">Plate</th>
                    <th className="pb-3 pr-4 font-medium">Car</th>
                    <th className="pb-3 pr-4 font-medium">Inspector</th>
                    <th className="pb-3 pr-4 font-medium text-right">Mileage</th>
                    <th className="pb-3 pr-4 font-medium text-right">Δ km</th>
                    <th className="pb-3 pr-4 font-medium">Status</th>
                    <th className="pb-3 pr-4 font-medium">Result</th>
                    <th className="pb-3 pr-4 font-medium">Next Due</th>
                    <th className="pb-3 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {paginatedInspections.map(ins => (
                    <tr key={ins.id} className="hover:bg-gray-50 transition-colors">
                      <td className="py-3 pr-4 text-gray-600 whitespace-nowrap">{formatDate(ins.inspection_date)}</td>
                      <td className="py-3 pr-4 font-semibold text-gray-900 whitespace-nowrap">{ins.plate_no?.toUpperCase()}</td>
                      <td className="py-3 pr-4 text-gray-600 whitespace-nowrap">{[ins.make, ins.model, ins.year].filter(Boolean).join(' ') || '—'}</td>
                      <td className="py-3 pr-4 text-gray-600">{ins.inspector_name || '—'}</td>
                      <td className="py-3 pr-4 text-right text-gray-600 whitespace-nowrap">{ins.mileage ? `${ins.mileage.toLocaleString()} km` : '—'}</td>
                      <td className="py-3 pr-4 text-right text-gray-600 whitespace-nowrap">{ins.distance_since_last != null ? `+${ins.distance_since_last.toLocaleString()}` : '—'}</td>
                      <td className="py-3 pr-4">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${statusBadgeClass(ins.inspection_status)}`}>{statusLabel(ins.inspection_status)}</span>
                      </td>
                      <td className="py-3 pr-4">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${resultBadgeClass(ins.result)}`}>{resultLabel(ins.result)}</span>
                      </td>
                      <td className="py-3 pr-4 text-gray-600 whitespace-nowrap">{formatDate(ins.next_inspection_due)}</td>
                      <td className="py-3">
                        <ActionButtons
                          inspection={ins}
                          onPDF={handleDownloadPDF}
                          onRetryDrive={handleRetryDriveSetup}
        
                          onConduct={(ins) => navigate(`/inspections/internal/${ins.id}/conduct`)}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            </div>
          </div>

          {/* Pagination */}
          {filteredInspections.length > PAGE_SIZE && (
            <div className="mt-4 bg-white rounded-xl border border-gray-100 shadow-sm px-4 py-3 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-gray-600">
              <span>Showing {showingFrom}–{showingTo} of {filteredInspections.length}</span>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1.5 border border-gray-300 rounded disabled:opacity-40 hover:bg-gray-50 disabled:cursor-not-allowed"
                >
                  ←
                </button>

                {(() => {
                  const pages = [];
                  const delta = 2;
                  const left  = Math.max(1, currentPage - delta);
                  const right = Math.min(totalPages, currentPage + delta);

                  if (left > 1) {
                    pages.push(<button key={1} onClick={() => setCurrentPage(1)} className="px-3 py-1.5 border border-gray-300 rounded hover:bg-gray-50">1</button>);
                    if (left > 2) pages.push(<span key="l…" className="px-1">…</span>);
                  }
                  for (let i = left; i <= right; i++) {
                    pages.push(
                      <button
                        key={i}
                        onClick={() => setCurrentPage(i)}
                        className={`px-3 py-1.5 border rounded ${i === currentPage ? 'bg-brand-primary text-white border-brand-primary' : 'border-gray-300 hover:bg-gray-50'}`}
                      >{i}</button>
                    );
                  }
                  if (right < totalPages) {
                    if (right < totalPages - 1) pages.push(<span key="r…" className="px-1">…</span>);
                    pages.push(<button key={totalPages} onClick={() => setCurrentPage(totalPages)} className="px-3 py-1.5 border border-gray-300 rounded hover:bg-gray-50">{totalPages}</button>);
                  }
                  return pages;
                })()}

                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1.5 border border-gray-300 rounded disabled:opacity-40 hover:bg-gray-50 disabled:cursor-not-allowed"
                >
                  →
                </button>
              </div>
            </div>
          )}

        </>
      )}
    </div>
  );
};

export default InternalInspections;
